import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Input, InputNumber, Select, Space, Table } from 'antd';
import SettingService, { SettingItem } from '../../services/SettingService';
import { openNotification, formatToTRY, isAccountActive } from '../../utils';
import UserService from '../../services/UserService';

type EditableRow = SettingItem & { pending?: boolean };

const SERVICE_FEE_KEY = 'SERVICE_FEE';
const PROPOSAL_MIN_UNIT_PRICE_KEY = 'PROPOSAL_MIN_UNIT_PRICE';
const PROPOSAL_INCREASE_EVALUATION_LIMIT_KEY = 'PROPOSAL_INCREASE_EVALUATION_LIMIT';
const PROPOSAL_VERSION_KEY = 'PROPOSAL_VERSION';

const PROPOSAL_TASK_DURATION_KEY_ORDER = [
  'PROPOSAL_TASK_OFFER_CONTACT_DURATION_MINUTES',
  'PROPOSAL_TASK_CONTRACT_REVIEW_DURATION_MINUTES',
  'PROPOSAL_TASK_CONTRACT_UPLOAD_DURATION_MINUTES',
  'PROPOSAL_TASK_PLANNING_DURATION_MINUTES',
  'PROPOSAL_TASK_LEGAL_NOTICE_DURATION_MINUTES',
  'PROPOSAL_TASK_FINANCE_COLLECTION_DURATION_MINUTES',
  'PROPOSAL_TASK_REPORT_CONTROL_DURATION_MINUTES',
] as const;

const ASSIGNEE_KEY_ORDER = [
  'PROPOSAL_DEFAULT_OFFER_ASSIGNE_USER_ID',
  'PROPOSAL_DEFAULT_PLAN_ASSIGNE_USER_ID',
  'PROPOSAL_DEFAULT_LEGAL_ASSIGNE_USER_ID',
  'PROPOSAL_DEFAULT_REPORT_ASSIGNE_USER_ID',
  'PROPOSAL_DEFAULT_INVOICE_ASSIGNE_USER_ID',
  'PROPOSAL_DEFAULT_FINANCE_ASSIGNE_USER_ID',
] as const;

const NUMERIC_SETTING_CONFIG: Record<string, { min: number; max?: number; step: number; addonAfter: string }> = {
  [SERVICE_FEE_KEY]: { min: 0, step: 10, addonAfter: 'TL' },
  [PROPOSAL_MIN_UNIT_PRICE_KEY]: { min: 10, step: 1, addonAfter: 'TL' },
  [PROPOSAL_INCREASE_EVALUATION_LIMIT_KEY]: { min: 0, max: 100, step: 1, addonAfter: '%' },
  PROPOSAL_TASK_OFFER_CONTACT_DURATION_MINUTES: { min: 0, step: 1, addonAfter: 'dk' },
  PROPOSAL_TASK_CONTRACT_REVIEW_DURATION_MINUTES: { min: 0, step: 1, addonAfter: 'dk' },
  PROPOSAL_TASK_CONTRACT_UPLOAD_DURATION_MINUTES: { min: 0, step: 1, addonAfter: 'dk' },
  PROPOSAL_TASK_PLANNING_DURATION_MINUTES: { min: 0, step: 1, addonAfter: 'dk' },
  PROPOSAL_TASK_LEGAL_NOTICE_DURATION_MINUTES: { min: 0, step: 1, addonAfter: 'dk' },
  PROPOSAL_TASK_FINANCE_COLLECTION_DURATION_MINUTES: { min: 0, step: 1, addonAfter: 'dk' },
  PROPOSAL_TASK_REPORT_CONTROL_DURATION_MINUTES: { min: 0, step: 1, addonAfter: 'dk' },
};

const PRIORITY_ORDER = [
  SERVICE_FEE_KEY,
  PROPOSAL_MIN_UNIT_PRICE_KEY,
  PROPOSAL_INCREASE_EVALUATION_LIMIT_KEY,
  PROPOSAL_VERSION_KEY,
  ...PROPOSAL_TASK_DURATION_KEY_ORDER,
  ...ASSIGNEE_KEY_ORDER,
] as const;

const PRIORITY_KEY_SET = new Set<string>(PRIORITY_ORDER);
const ASSIGNEE_KEY_SET = new Set<string>(ASSIGNEE_KEY_ORDER);

const reorderSettings = (items: SettingItem[]): SettingItem[] => {
  const remaining: SettingItem[] = [];
  const prioritized: Record<string, SettingItem> = {};

  items.forEach((item) => {
    if (item.key && PRIORITY_KEY_SET.has(item.key)) {
      prioritized[item.key] = item;
    } else {
      remaining.push(item);
    }
  });

  const ordered = PRIORITY_ORDER.reduce<SettingItem[]>((acc, key) => {
    if (prioritized[key]) {
      acc.push(prioritized[key]);
    }
    return acc;
  }, []);

  return [...ordered, ...remaining];
};

const SettingKeyList: React.FC = () => {
  const [data, setData] = useState<EditableRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState<any[]>([]);
  const [assignableUsersAll, setAssignableUsersAll] = useState<any[]>([]);
  const [initialValueMap, setInitialValueMap] = useState<Record<string, string>>({});

  useEffect(() => {
    load();
    loadAssignableUsers();
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const list = await SettingService.listSettings();
      const ordered = reorderSettings(list);
      setData(ordered);
      const baseMap: Record<string, string> = {};
      ordered.forEach((item) => {
        baseMap[item.key] = item.value ?? '';
      });
      setInitialValueMap(baseMap);
    } catch (e: any) {
      openNotification('error', 'Hata', e?.message || 'Ayarlar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (record: EditableRow) => {
    let normalizedValue = record.value ?? null;
    const numericConfig = NUMERIC_SETTING_CONFIG[record.key];

    if (numericConfig) {
      const parsedValue = Number(record.value);
      if (Number.isNaN(parsedValue)) {
        openNotification('warning', 'Geçersiz Değer', `${record.title || record.key} sayısal olmalıdır`);
        return;
      }
      if (parsedValue < numericConfig.min) {
        openNotification(
          'warning',
          'Geçersiz Değer',
          `${record.title || record.key} en az ${numericConfig.min} ${numericConfig.addonAfter} olmalıdır`,
        );
        return;
      }
      if (numericConfig.max != null && parsedValue > numericConfig.max) {
        openNotification(
          'warning',
          'Geçersiz Değer',
          `${record.title || record.key} en fazla ${numericConfig.max} ${numericConfig.addonAfter} olmalıdır`,
        );
        return;
      }
      normalizedValue = String(parsedValue);
    }

    setData((prev) => prev.map((r) => (r.key === record.key ? { ...r, pending: true } : r)));
    try {
      const saved = await SettingService.updateSetting(record.key, normalizedValue);
      setData((prev) => prev.map((r) => (r.key === record.key ? { ...saved, pending: false } : r)));
      setInitialValueMap((prev) => ({ ...prev, [record.key]: saved.value ?? '' }));
      openNotification('success', 'Başarılı', 'Ayar kaydedildi');
    } catch (e: any) {
      openNotification('error', 'Hata', e?.message || 'Ayar kaydedilemedi');
      setData((prev) => prev.map((r) => (r.key === record.key ? { ...r, pending: false } : r)));
    }
  };

  const isRowDirty = (record: EditableRow) => {
    return (record.value ?? '') !== (initialValueMap[record.key] ?? '');
  };

  const handleResetRow = (record: EditableRow) => {
    setData((prev) =>
      prev.map((row) =>
        row.key === record.key
          ? { ...row, value: initialValueMap[record.key] ?? '' }
          : row,
      ),
    );
  };

  const loadAssignableUsers = async () => {
    try {
      const resp: any = await UserService.getUsersWithoutPagination({});
      const list: any[] = resp?.data?.content ?? resp?.data ?? [];
      const roleFiltered = list.filter((u) => {
        const roles: any[] = u?.roles ?? [];
        const roleNames = roles.map((r) => r?.name).filter(Boolean);
        return !roleNames.includes('INSPECTOR') && !roleNames.includes('CUSTOMER');
      });
      const activeOnly = roleFiltered.filter((u) => isAccountActive(u));
      setAssignableUsersAll(roleFiltered);
      setAssignableUsers(activeOnly);
    } catch (e) {
      console.error('Assignable users could not be loaded', e);
    }
  };

  const renderValueInput = (record: EditableRow) => {
    const numericConfig = NUMERIC_SETTING_CONFIG[record.key];
    if (numericConfig) {
      return (
        <InputNumber
          style={{ width: '100%' }}
          value={record.value != null && record.value !== '' ? Number(record.value) : undefined}
          onChange={(val) => {
            const value = val == null ? '' : String(val);
            setData((prev) => prev.map((r) => (r.key === record.key ? { ...r, value } : r)));
          }}
          min={numericConfig.min}
          max={numericConfig.max}
          step={numericConfig.step}
          addonAfter={numericConfig.addonAfter}
        />
      );
    }
    if (ASSIGNEE_KEY_SET.has(record.key)) {
      const currentUserId = Number(record.value);
      const options = [...assignableUsers];
      if (!Number.isNaN(currentUserId) && currentUserId > 0) {
        const exists = options.some((user) => Number(user?.id) === currentUserId);
        if (!exists) {
          const fallbackUser =
            assignableUsersAll.find((user) => Number(user?.id) === currentUserId) ||
            {
              id: currentUserId,
              firstName: 'Kullanıcı',
              lastName: `#${currentUserId}`,
            };
          options.push(fallbackUser);
        }
      }
      return (
        <Select
          style={{ width: '100%' }}
          showSearch
          allowClear
          placeholder="Kullanıcı seçiniz"
          optionFilterProp="children"
          value={record.value ?? undefined}
          onChange={(val) =>
            setData((prev) => prev.map((r) => (r.key === record.key ? { ...r, value: val || '' } : r)))
          }
          filterOption={(input, option) =>
            (() => {
              let label: string | undefined;
              if (typeof option?.children === 'string') {
                label = option.children;
              } else if (option?.children && typeof option.children === 'object' && 'toString' in option.children) {
                label = (option.children as any).toString();
              }
              return label ? label.toLowerCase().includes(input.toLowerCase()) : false;
            })()
          }
        >
          {options.map((user) => {
            const userId = Number(user?.id);
            if (Number.isNaN(userId) || userId <= 0) {
              return null;
            }
            return (
              <Select.Option key={userId} value={String(userId)}>
                {`${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || user.email || user.id}
              </Select.Option>
            );
          })}
        </Select>
      );
    }
    return (
      <Input
        value={record.value ?? ''}
        onChange={(e) => {
          const { value } = e.target;
          setData((prev) => prev.map((r) => (r.key === record.key ? { ...r, value } : r)));
        }}
        placeholder="Değer giriniz"
      />
    );
  };

  const columns = useMemo(
    () => [
      {
        title: 'Başlık',
        dataIndex: 'title',
        key: 'title',
      },
      {
        title: 'Değer',
        key: 'value',
        render: (_: any, record: EditableRow) => renderValueInput(record),
      },
      {
        title: 'İşlem',
        key: 'actions',
        width: 220,
        render: (_: any, record: EditableRow) => (
          <Space>
            {isRowDirty(record) && (
              <Button type="default" className='ant-btn-primary' onClick={() => handleResetRow(record)} disabled={record.pending}>
                Vazgeç
              </Button>
            )}
            <Button loading={record.pending} type="primary" onClick={() => handleSave(record)}>
              Kaydet
            </Button>
          </Space>
        ),
      },
    ],
    [assignableUsers, initialValueMap],
  );

  return (
    <Card title="Sistem Ayarları">
      <Table
        rowKey="key"
        loading={loading}
        dataSource={data}
        columns={columns as any}
        pagination={false}
      />
    </Card>
  );
};

export default SettingKeyList;
